import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmail, 
  signUpWithEmail, 
  signInWithGoogle, 
  signOutUser, 
  resetPassword, 
  onAuthStateChange 
} from '../services/firebase';
import { trackUserAction } from '../utils/analytics';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChange((user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (credentials) => {
    try {
      const result = await signInWithEmail(credentials.email, credentials.password);
      if (result.success) {
        trackUserAction.userSignedIn();
        return { success: true };
      } else {
        return { success: false, message: result.error };
      }
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const register = async (userData) => {
    try {
      const result = await signUpWithEmail(
        userData.reg_email || userData.email, 
        userData.reg_password || userData.password, 
        userData.reg_name || userData.name
      );
      if (result.success) {
        trackUserAction.userRegistered();
        return { success: true };
      } else {
        return { success: false, message: result.error };
      }
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const loginWithGoogle = async () => {
    try {
      const result = await signInWithGoogle();
      if (result.success) {
        trackUserAction.userSignedIn();
        return { success: true };
      } else {
        return { success: false, message: result.error };
      }
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const logout = async () => {
    try {
      const result = await signOutUser();
      if (result.success) {
        setUser(null);
        return { success: true };
      } else {
        return { success: false, message: result.error };
      }
    } catch (error) {
      console.error('Logout error:', error);
      setUser(null); // Still clear user state even if logout fails
      return { success: true };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const result = await resetPassword(email);
      if (result.success) {
        return { success: true, message: 'Password reset email sent!' };
      } else {
        return { success: false, message: result.error };
      }
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    loginWithGoogle,
    logout,
    forgotPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 